// The upload status in plain words — the uploader's default view, for a photographer at an event who
// wants to know: are my photos up, and is it finished.
//
// Derived from the detailed model (uploadTimeline.ts), which still drives the "Show details" swimlane:
//
//   one short sentence    "Uploading — about 2 min left" / "Live — saving originals…" / "Done — 5,001 photos live"
//                         "Paused — reconnecting" / "3 photos failed"
//   a row per stage       Previews 3,214 / 5,001   ·   Originals 1,200 / 5,001   (a plain upload: Photos 120 / 400)
//   a hint, sometimes     "2 photos people bought go first"
//   at most one button    Retry / Resume / Sign in / Add folder again
//   notes                 everything else (why, what next, selfie search) — shown behind "Show details"
//
// Counts are always photos, per stage: never a total that adds previews and originals together. The
// second pass is "originals" (never "full-size" / "full-quality"). No MB/s or photos/min here.
// Pure: everything comes from the timeline (+ whether the browser is online).
import { fmtCount, originalsLaterStatus, type StageState, type TimelineStage, type UploadTimeline } from "./uploadTimeline";
import { formatOriginalsBy } from "./originalsBy";
import { failLabel, topReasons } from "./failReasons";

export type StageRowKey = "previews" | "originals" | "photos";

/** One stage's progress: "Previews 3,214 / 5,001". */
export interface StageRow {
  key: StageRowKey;
  /** "Previews" / "Originals" / "Photos" / "Videos". */
  label: string;
  state: StageState;
  done: number;
  total: number;
  /** % for the thin bar (100 when finished). */
  pct: number;
  /** A few muted words after the count: "about 5 min left", "paused", "next"… */
  note?: string;
}

export type SimpleActionKind = "retry" | "resume" | "sign-in" | "add-again";

export interface SimpleStatus {
  /** Drives the sentence's icon (the text always says it too). */
  tone: StageState;
  sentence: string;
  /** Something went wrong (failed photos, signed out) — the sentence reads as a problem. */
  problem: boolean;
  rows: StageRow[];
  /** "2 photos people bought go first" — while their originals are being sent ahead of the rest. */
  hint?: string;
  /** The one thing to do, if anything. */
  action?: { kind: SimpleActionKind; label: string };
  /** Secondary sentences for "Show details": what went wrong / what's next / selfie search. */
  notes: string[];
}

export interface SimpleOptions {
  /** navigator.onLine — offline while running reads "Paused — reconnecting". Default true. */
  online?: boolean;
  /** Is the gallery published? Unknown → treated as live once the photos are in. */
  published?: boolean;
  /** An auto-publish is armed for when the photos are in. */
  autoPublishArmed?: boolean;
  /** "Chrome 141" — names the browser in a failure reason (default: this browser). */
  browser?: string;
}

/** Seconds → "about 2 min left" / "less than a minute left" / "about 1 hr 10 min left"; "" if unknown. */
export const fmtTimeLeft = (secs: number | null | undefined): string => {
  if (secs == null || !isFinite(secs) || secs <= 0) return "";
  if (secs < 50) return "less than a minute left";
  const mins = Math.max(1, Math.round(secs / 60));
  if (mins < 60) return `about ${mins} min left`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `about ${h} hr${m ? ` ${m} min` : ""} left`;
};

/** Seconds → "40 s" / "2 min". */
const fmtIn = (secs: number) => (secs < 60 ? `${Math.max(1, Math.round(secs))} s` : `${Math.round(secs / 60)} min`);

const photos = (n: number, noun = "photo") => `${fmtCount(n)} ${n === 1 ? noun : `${noun}s`}`;
const running = (s?: TimelineStage) => !!s && (s.state === "active" || s.state === "paused" || s.state === "waiting");
const pctOf = (s: TimelineStage) => (s.state === "done" ? 100 : Math.max(0, Math.min(100, s.pct ?? (s.total ? Math.round((s.done / s.total) * 100) : 0))));

/** The note shared by every stage row for the states that aren't "under way" or "finished". */
const stateNote = (s: TimelineStage): string | undefined => {
  switch (s.state) {
    case "paused":
      return "paused";
    case "waiting":
      return "on hold";
    case "cancelled":
      return "stopped";
    case "failed":
      return "didn't upload";
    default:
      // Failures are the sentence's to say ("3 photos failed"), once.
      return undefined;
  }
};

/** The first pass: "Previews 3,214 / 5,001" (or "Photos" / "Videos" for an ordinary upload). */
const galleryRow = (s: TimelineStage, key: StageRowKey, label: string): StageRow => {
  const gettingReady = s.state === "active" && s.done === 0 && s.stateLabel === "Getting ready";
  return {
    key,
    label,
    state: s.state,
    done: s.done,
    total: s.total,
    pct: pctOf(s),
    note: gettingReady ? "getting ready…" : stateNote(s),
  };
};

/** The second pass: "Originals 1,200 / 5,001" — the untouched files downloads and prints come from. */
const originalsRow = (s: TimelineStage, photosTotal: number): StageRow => {
  const total = s.total || photosTotal;
  const row = (state: StageState, note?: string): StageRow => ({
    key: "originals", label: "Originals", state, done: s.done, total, pct: state === "done" ? 100 : pctOf(s), note,
  });
  // "Upload previews now, originals later": promised for later, not sent from here.
  if (s.stateLabel === "Later" && s.at) return row("pending", `expected by ${formatOriginalsBy(s.at)}`);
  switch (s.state) {
    case "active":
      return row("active", stateNote(s) ?? (fmtTimeLeft(s.etaSec) || undefined));
    case "waiting":
      if (s.stateLabel === "Signed out") return row("waiting", "on hold");
      // Not on hold: just next in line (after the previews are in), or about to start.
      return s.stateLabel === "Starting" || s.total > 0 ? row("active", "starting…") : row("pending", "next");
    case "skipped":
      return row("skipped", "not sent");
    default:
      return row(s.state, stateNote(s));
  }
};

/** The plain-words status of one folder's upload. */
export const simpleStatus = (tl: UploadTimeline, opts: SimpleOptions = {}): SimpleStatus => {
  const online = opts.online !== false;
  const by = (k: TimelineStage["key"]) => tl.stages.find((s) => s.key === k);
  const plain = by("upload");
  const prev = by("previews");
  const orig = plain ? undefined : by("originals");
  const scan = by("scan");
  const noun = plain && /video/i.test(plain.label) ? "video" : "photo";
  const gallery = plain ?? prev;

  // --- rows: one per stage, counted in photos
  const rows: StageRow[] = [];
  if (plain) rows.push(galleryRow(plain, "photos", noun === "video" ? "Videos" : "Photos"));
  else if (prev) rows.push(galleryRow(prev, "previews", "Previews"));
  // Originals only where a separate pass exists (a preview-first upload, or one finishing the originals an
  // earlier one left owed). An ordinary upload's files ARE the originals.
  if (orig) rows.push(originalsRow(orig, prev?.total ?? 0));
  const sold = tl.soldWaiting;
  // Photos that didn't go through are tried again by themselves first: say so (and when) instead of "failed".
  const retrying = tl.retrying;
  const hint = retrying
    ? `Trying ${photos(retrying.count, noun)} again ${retrying.inSec > 0 ? `in ${fmtIn(retrying.inSec)}` : "now"} — they didn't go through`
    : orig?.state === "active" && sold > 0 ? `${photos(sold)} people bought ${sold === 1 ? "goes" : "go"} first` : undefined;

  // --- live: published galleries show each photo as it lands. Unknown → live once the photos are in.
  const notes: string[] = [];
  let isLive = false;
  if (gallery) {
    if (gallery.done === 0 && !running(gallery)) notes.push("Not live — nothing made it into the gallery.");
    else if (opts.published === false) {
      notes.push(
        opts.autoPublishArmed
          ? running(gallery) ? "The gallery goes live as soon as the previews are in." : "Going live now…"
          : "Not live yet — publish the gallery so buyers can see it.",
      );
    } else if (gallery.done > 0 && (opts.published === true || by("live")?.state === "done" || (!by("live") && gallery.state === "done"))) {
      isLive = true;
    }
  }

  // Photos that didn't make it, across the stages (a photo whose preview failed is never sent again as an
  // original, so the two counts don't overlap — this is a count of photos).
  const failed = (gallery?.failed ?? 0) + (orig?.failed ?? 0);
  const inGallery = gallery?.done ?? 0;
  // The real reason (the most common one), never a guess: "214 photos didn't upload: the uploads lost their connection (network)".
  const top = topReasons(tl.failReasons)[0]?.[0];
  const failedNote = `${photos(failed, noun)} didn't upload${top ? `: ${failLabel(top, opts.browser)}` : ""}. Retry them — anything already up is skipped.`;
  const addAgain = { kind: "add-again" as const, label: "Add folder again" };
  const retry = { kind: "retry" as const, label: "Retry" };

  let tone: StageState;
  let sentence: string;
  let problem = false;
  let action: SimpleStatus["action"];
  if (tl.signedOut) {
    tone = "waiting";
    sentence = "Signed out — uploads are on hold";
    problem = true;
    action = { kind: "sign-in", label: "Sign in" };
    notes.unshift("Your sign-in expired. Sign in again and the upload carries on — nothing is lost.");
  } else if (tl.running && !online) {
    tone = "paused";
    sentence = "Paused — reconnecting";
    notes.unshift("You're offline. The upload carries on by itself when the connection is back.");
  } else if (tl.paused) {
    tone = "paused";
    sentence = "Paused";
    action = { kind: "resume", label: "Resume" };
    notes.unshift("Nothing more is sent until you resume.");
  } else if (tl.running || orig?.state === "waiting" || orig?.state === "active") {
    // Still going — including the moment between the previews landing and the originals pass being
    // queued (nothing is "running" then, but it isn't done either).
    tone = "active";
    if (gallery && running(gallery)) {
      const left = fmtTimeLeft(gallery.etaSec);
      sentence = left ? `Uploading — ${left}` : "Uploading…";
    } else if (!gallery) {
      const left = orig ? fmtTimeLeft(orig.etaSec) : "";
      sentence = left ? `Saving originals — ${left}` : "Saving originals…";
    } else {
      sentence = isLive ? "Live — saving originals…" : "Previews up — saving originals…";
    }
    // Photos that already failed can be retried while the rest carries on.
    if (failed > 0 && gallery && !running(gallery)) {
      action = retry;
      notes.unshift(failedNote);
    }
  } else if (tl.cancelled && !tl.complete) {
    tone = "cancelled";
    sentence = gallery ? `Stopped — ${photos(inGallery, noun)} in your gallery` : "Stopped";
    action = addAgain;
    notes.unshift("The rest weren't sent. Add the folder again to finish — anything already up is skipped.");
  } else if (failed > 0) {
    tone = "failed";
    sentence = `${photos(failed, noun)} failed`;
    problem = true;
    action = retry;
    notes.unshift(failedNote);
  } else if (orig && orig.state === "skipped") {
    // The previews finished but this queue didn't send the originals.
    tone = "waiting";
    sentence = `${isLive ? "Live" : "Uploaded"} — ${photos(inGallery, noun)} in your gallery`;
    action = addAgain;
    notes.unshift("Some originals may still be owed. Add the folder again to finish — anything already up is skipped.");
  } else if (orig && orig.stateLabel === "Later" && orig.at && gallery && gallery.done > 0) {
    // "Upload previews now, originals later": the previews are the whole job for now.
    tone = "done";
    sentence = originalsLaterStatus(orig.at);
    notes.unshift("Add the same folder later, from any device, to upload the originals. Photos that are already up are skipped.");
  } else if (!gallery) {
    tone = "done";
    sentence = `Done — ${photos(orig?.done ?? 0, "original")} saved`;
  } else {
    tone = "done";
    sentence = opts.published === false ? `Done — ${photos(inGallery, noun)} uploaded` : `Done — ${photos(inGallery, noun)} live`;
  }

  // Photos the server already had count as done — say so, so the numbers add up (they're never failures).
  if (tl.alreadyThere > 0) {
    notes.push(`${photos(tl.alreadyThere, noun)} ${tl.alreadyThere === 1 ? "was" : "were"} already on our servers — counted as done.`);
  }

  // Selfie search: only when this browser is finding faces itself (otherwise our servers do it as each
  // photo lands and there's nothing here to wait for).
  if (scan && scan.state !== "skipped" && scan.total > 0) {
    notes.push(scan.state === "done" ? "Selfie search: ready." : "Selfie search: getting ready.");
  }

  return { tone, sentence, problem, rows, hint, action, notes };
};

/** The rows in one line, for a collapsed queue: "Previews 3,214 / 5,001 · Originals 1,200 / 5,001". */
export const rowsSummary = (rows: readonly StageRow[]): string =>
  rows.map((r) => `${r.label} ${fmtCount(r.done)} / ${fmtCount(r.total)}`).join(" · ");
